/**
 * A11yPanel — гранулярная панель accessibility (Фаза 9, П2).
 *
 * Кнопка-значок accessibility в шапке `.app-topbar` (рядом с `ThemeToggle`),
 * открывает popover с независимыми настройками (по одной на каждый `data-a11y-*`
 * атрибут на `<html>` + `--font-scale`):
 *
 *  1. Размер шрифта — степы A−/A/A+/A++ (0.9 / 1.0 / 1.15 / 1.3) → `--font-scale`.
 *  2. Высокая контрастность — toggle → `data-a11y-contrast="high"`.
 *  3. Уменьшить анимацию — toggle → `data-a11y-motion="reduce"`.
 *  4. Шрифт для дислексии — toggle → `data-a11y-dyslexia="on"`.
 *  5. Подчёркивать ссылки — toggle → `data-a11y-underline="on"`.
 *  6. Сбросить — кнопка → `resetA11y()` (все в дефолт).
 *
 * Состояние читается из localStorage (`kikayoo:a11y`) через `a11ySettings.ts` при
 * первом рендере; каждое изменение → `applyA11yAttrs` (DOM + сохранение). Инлайн-
 * скрипт в `index.html` применяет те же атрибуты **до** рендера React — без вспышки.
 *
 * Доступность панели:
 *  - кнопка: `aria-expanded` / `aria-controls` / `aria-haspopup="dialog"` / `:focus-visible`;
 *  - popover: `role="dialog"` + `aria-label`, при открытии фокус уходит на первый
 *    фокусируемый контроль, при закрытии (Escape / клик вне) возвращается на кнопку;
 *  - степы шрифта: `role="radiogroup"` + `role="radio"` + `aria-checked`;
 *  - тогглы: `role="switch"` + `aria-checked`;
 *  - `aria-live="polite"` рядом с панелью озвучивает применённые изменения.
 *
 * Экономно: inline-SVG (♿-подобный «человечек в круге»), ноль тяжёлых библиотек/шрифтов.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyA11yAttrs,
  FONT_SIZE_STEPS,
  FONT_STEP_LABELS,
  loadA11y,
  resetA11y,
  type A11ySettings,
} from './a11ySettings';

/** Селекторы для фокус-менеджмента popover (первый фокусируемый элемент внутри). */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function A11yPanel(): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // На первом рендере берём то, что уже применил инлайн-скрипт (или дефолты), чтобы
  // состояние кнопок совпадало с DOM без лишнего applyA11yAttrs.
  const [settings, setSettings] = useState<A11ySettings>(() => loadA11y());
  // Строка последнего применённого изменения для aria-live (незрящие пользователи
  // услышат подтверждение, что настройка применилась).
  const [announce, setAnnounce] = useState('');

  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Флаг «popover уже открывался» — нужен, чтобы при закрытии вернуть фокус на
  // кнопку (а не при первичном mount, когда open=false).
  const wasOpenRef = useRef(false);

  // Escape (закрытие) + mousedown вне popover/кнопки (закрытие). Подписка только
  // пока popover открыт; cleanup снимает слушатели, чтобы не ловить клики в фоне.
  // mousedown (а не click) — закрываем до того, как клик «провалится» в элемент под
  // popover. stopPropagation на Escape — чтобы родительские диалоги не закрывались.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    const onPointerDown = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (!target) return;
      if (popoverRef.current?.contains(target)) return;
      if (btnRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  // Фокус-менеджмент: при открытии — фокус на первый контроль popover; при закрытии
  // (если до этого был открыт) — возврат на кнопку-значок. Это ключевой a11y-контракт
  // для popover/dialog: зрячий кликнул → фокус в панель; закрыл — фокус не «теряется».
  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      // requestAnimationFrame: ждём отрисовки popover, чтобы querySelector нашёл узел.
      requestAnimationFrame(() => {
        const node = popoverRef.current;
        if (!node) return;
        const focusable = node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (focusable ?? node).focus();
      });
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      btnRef.current?.focus();
    }
  }, [open]);

  // Применяет патч к состоянию: мерж + applyA11yAttrs (DOM + сохранение) + анонс.
  const update = useCallback((patch: Partial<A11ySettings>, label: string): void => {
    setSettings((prev) => {
      const next = { ...prev, ...patch };
      applyA11yAttrs(next);
      return next;
    });
    setAnnounce(label);
  }, []);

  const handleReset = useCallback((): void => {
    const next = resetA11y();
    setSettings(next);
    setAnnounce(t('a11y.reset'));
  }, [t]);

  return (
    <div className="a11y-panel">
      <button
        type="button"
        ref={btnRef}
        className="btn btn--ghost a11y-panel__btn"
        aria-expanded={open}
        aria-controls="a11y-popover"
        aria-haspopup="dialog"
        aria-label={t('a11y.buttonLabel')}
        title={t('a11y.buttonLabel')}
        onClick={() => setOpen((v) => !v)}
        data-testid="a11y-panel-btn"
      >
        {/* Inline-SVG «universal access» — человечек в круге. Без зависимостей. */}
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="4" r="1.6" fill="currentColor" stroke="none" />
          <path d="M4 9h16M12 9v5m0 0l-3 7m3-7l3 7" />
        </svg>
      </button>

      {/* aria-live: озвучивает применённые изменения (screen reader). polite — не
          перебивает текущее объявление. Визуально скрыт через sr-only-стиль класса. */}
      <span className="sr-only" role="status" aria-live="polite">
        {announce}
      </span>

      {open ? (
        <div
          id="a11y-popover"
          ref={popoverRef}
          role="dialog"
          aria-modal="false"
          aria-label={t('a11y.title')}
          tabIndex={-1}
          className="a11y-popover"
          data-testid="a11y-popover"
        >
          <p className="a11y-popover__title">{t('a11y.title')}</p>

          {/* 1. Размер шрифта — радиогруппа из 4 степов. */}
          <fieldset className="a11y-group">
            <legend className="a11y-group__legend">{t('a11y.fontSize')}</legend>
            <div className="a11y-stepper" role="radiogroup" aria-label={t('a11y.fontSize')}>
              {FONT_SIZE_STEPS.map((step) => {
                const active = settings.fontSize === step;
                return (
                  <button
                    key={step}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`btn btn--ghost a11y-step${active ? ' a11y-step--active' : ''}`}
                    onClick={() =>
                      update({ fontSize: step }, `${t('a11y.fontSize')}: ${FONT_STEP_LABELS[step]}`)
                    }
                    data-testid={`a11y-font-${step}`}
                  >
                    {FONT_STEP_LABELS[step]}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {/* 2–5. Toggle-настройки (role="switch" + aria-checked). */}
          <ToggleRow
            checked={settings.highContrast}
            onChange={(v) => update({ highContrast: v }, t('a11y.highContrast'))}
            label={t('a11y.highContrast')}
            testId="a11y-contrast"
          />
          <ToggleRow
            checked={settings.reduceMotion}
            onChange={(v) => update({ reduceMotion: v }, t('a11y.reduceMotion'))}
            label={t('a11y.reduceMotion')}
            testId="a11y-motion"
          />
          <ToggleRow
            checked={settings.dyslexiaFont}
            onChange={(v) => update({ dyslexiaFont: v }, t('a11y.dyslexiaFont'))}
            label={t('a11y.dyslexiaFont')}
            testId="a11y-dyslexia"
          />
          <ToggleRow
            checked={settings.underlineLinks}
            onChange={(v) => update({ underlineLinks: v }, t('a11y.underlineLinks'))}
            label={t('a11y.underlineLinks')}
            testId="a11y-underline"
          />

          {/* 6. Сброс. */}
          <button
            type="button"
            className="btn a11y-reset"
            onClick={handleReset}
            data-testid="a11y-reset"
          >
            {t('a11y.reset')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Строка toggle-контроля: подпись слева + трек-переключатель справа. Реализован как
 * `<button role="switch">` (а не `<input type="checkbox">`) — даёт полный контроль
 * над стилизацией и соответствует ARIA-паттерну switch (on/off). `aria-checked`
 * меняется синхронно с React-state → скринридер озвучивает «включено/выключено».
 */
interface ToggleRowProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  testId: string;
}

function ToggleRow({ checked, onChange, label, testId }: ToggleRowProps): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`a11y-toggle${checked ? ' a11y-toggle--on' : ''}`}
      onClick={() => onChange(!checked)}
      data-testid={testId}
    >
      <span className="a11y-toggle__label">{label}</span>
      <span className="a11y-toggle__track" aria-hidden="true">
        <span className="a11y-toggle__thumb" />
      </span>
    </button>
  );
}
